import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Check, ChevronDown, Copy, WrapText } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { highlight, isHighlightable } from '../lib/highlight.js';
import { cx } from './primitives.js';
const COLLAPSE_AT = 18;
const COLLAPSED_LINES = 12;
/** Copy text to the clipboard; resolves false when unavailable. */
export async function copyText(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    }
    catch {
        return false;
    }
}
export function CopyButton({ text, label = 'Copy' }) {
    const [done, setDone] = useState(false);
    return (_jsx("button", { type: "button", className: "vk-iconbtn", "aria-label": done ? 'Copied' : label, title: done ? 'Copied' : label, onClick: async () => {
            if (await copyText(text)) {
                setDone(true);
                setTimeout(() => setDone(false), 1400);
            }
        }, children: done ? _jsx(Check, { size: 14 }) : _jsx(Copy, { size: 14 }) }));
}
/** Highlight only once the block is near the viewport; plain text renders immediately. */
function useLazyTokens(code, lang) {
    const ref = useRef(null);
    const [tokens, setTokens] = useState(null);
    useEffect(() => {
        if (!lang || !isHighlightable(lang))
            return;
        const el = ref.current;
        if (!el)
            return;
        let cancelled = false;
        const run = () => highlight(code, lang)
            .then((t) => {
            if (!cancelled)
                setTokens(t);
        })
            .catch(() => { });
        if (typeof IntersectionObserver === 'undefined') {
            run();
            return;
        }
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                io.disconnect();
                run();
            }
        }, { rootMargin: '400px' });
        io.observe(el);
        return () => {
            cancelled = true;
            io.disconnect();
        };
    }, [code, lang]);
    return { ref, tokens };
}
export function CodeLines({ code, tokens, from = 0, to, }) {
    const lines = code.split('\n');
    const end = to ?? lines.length;
    const out = [];
    for (let i = from; i < end; i++) {
        const line = tokens?.[i];
        out.push(_jsxs("span", { className: "vk-code__line", children: [line
                    ? line.map((t, j) => (_jsx("span", { className: cx(!!t.fontStyle && (t.fontStyle & 1) > 0 && 'vk-i', !!t.fontStyle && (t.fontStyle & 2) > 0 && 'vk-b'), style: t.light || t.dark
                            ? { '--sl': t.light, '--sd': t.dark }
                            : undefined, children: t.content }, j)))
                    : lines[i], '\n'] }, i));
    }
    return _jsx(_Fragment, { children: out });
}
export function CodeBlock({ code, lang, title, collapsible = true, showLineNumbers = false, }) {
    const { ref, tokens } = useLazyTokens(code, lang);
    const [wrap, setWrap] = useState(false);
    const lineCount = code.split('\n').length;
    const canCollapse = collapsible && lineCount > COLLAPSE_AT;
    const [open, setOpen] = useState(!canCollapse);
    return (_jsxs("div", { className: cx('vk-code', wrap && 'vk-code--wrap', showLineNumbers && 'vk-code--numbered'), ref: ref, children: [_jsxs("div", { className: "vk-code__bar", children: [_jsx("span", { className: "vk-code__lang", children: title ?? lang ?? 'text' }), _jsxs("span", { className: "vk-code__actions", children: [_jsx("button", { type: "button", className: cx('vk-iconbtn', wrap && 'is-on'), "aria-pressed": wrap, "aria-label": "Wrap lines", title: "Wrap lines", onClick: () => setWrap((w) => !w), children: _jsx(WrapText, { size: 14 }) }), _jsx(CopyButton, { text: code, label: "Copy code" })] })] }), _jsx("pre", { className: "vk-code__pre", tabIndex: 0, children: _jsx("code", { children: _jsx(CodeLines, { code: code, tokens: tokens, to: open ? undefined : COLLAPSED_LINES }) }) }), canCollapse ? (_jsxs("button", { type: "button", className: "vk-code__more", "aria-expanded": open, onClick: () => setOpen((o) => !o), children: [_jsx(ChevronDown, { size: 14, className: cx('vk-chev', open && 'is-open') }), open ? 'Collapse' : `Show all ${lineCount} lines`] })) : null] }));
}
//# sourceMappingURL=CodeBlock.js.map